import { z } from 'zod';
import { addressDocumentSchema } from './address';

export const productDocumentSchema = z.object({
  brand: z.string(),
  bumps: z.number(),
  categories: z.array(z.string()),
  condition: z.string(),
  country_code: z.string(),
  curated_lists: z.array(z.string()).optional(),
  staff_picks_tagged_at: z.number().optional(),
  created: z.number(),
  date_added: z.number(),
  date_updated: z.number(),
  description: z.string().optional(),
  description_html: z.string().optional(),
  meta_description: z.string().optional(),
  favorite_count: z.number().default(0),
  from_web: z.boolean(),
  id: z.string(),
  images: z.array(
    z.object({
      thumb: z.string(),
      full: z.string(),
    })
  ).default([]),
  is_draft: z.boolean(),
  is_fraudulent: z.boolean().default(false),
  is_expired: z.boolean().default(false),
  is_verified: z.boolean().default(false),
  is_featured: z.boolean().default(false),
  is_flat_rate: z.boolean(),
  is_auction: z.boolean().optional().default(false),
  is_auction_live: z.boolean().optional().default(false),
  integrations: z.object({
    source: z.string(),
    id: z.string(),
    date_synced: z.number(),
    last_synced: z.number()
  }).optional(),
  start_time: z.number().optional(),
  end_time: z.number().optional(),
  minimum_offer_price: z.number(),
  offers_enabled: z.boolean(),
  og_price: z.number(),
  // Optional per-listing eBay price override. When set, the GF-direct publish
  // path uses this instead of `price` when sending the listing to eBay.
  // Nullable so disabling the eBay toggle on edit explicitly clears the
  // stored value (Firestore writes the null and downstream readers treat
  // null and undefined the same way).
  ebay_price: z.number().nullable().optional(),
  // How to publish to eBay: 'draft' = Seller Hub draft for review (default,
  // safe), 'live' = live listing immediately via Trading API without asDraft.
  // Nullable for the same reason as ebay_price above.
  ebay_publish_mode: z.enum(['draft', 'live']).nullable().optional(),
  // Current eBay state for this listing — set by the publish/withdraw API
  // routes so the dashboard can show the right per-listing button
  // ("Send to eBay" vs "Remove from eBay") without an extra round-trip to
  // gearsync per row.
  ebay_status: z.enum(['queued', 'live', 'withdrawn']).optional(),
  old_id: z.number(),
  old_seller_id: z.number(),
  on_sale: z.boolean(),
  on_vacation: z.boolean().optional(),
  out_of_stock: z.boolean(),
  // api#295: explicit soft-delete tombstone, set by deleteListing(). Since
  // #1422, deleteListing() no longer erases date_added (that was needed to
  // keep Algolia sync intact across a delete -> renew cycle), so this field
  // is the authoritative "was this deleted" signal for both web and api.
  // Nullable/optional: absent or null both mean "not deleted."
  deleted_at: z.number().nullable().optional(),
  price: z.number(),
  seller_id: z.string(),
  sold_date: z.number().optional(),
  // api#307: when a partner restocks a product after it sold, the api repo
  // moves sold_date's value here so shopping-feed / ad-customizer "is this
  // in stock" checks stop reading a back-in-stock listing as sold. Treated
  // exactly like sold_date by the delete-draft guard and rules blocklist —
  // it's still real sales history.
  last_sold_date: z.number().optional(),
  source: z.string(),
  account_id: z.string().optional(), //old sellers don't have this
  shipment: z.object({
    ship_from: addressDocumentSchema,
    package: z.object({
      weight: z.object({ unit: z.literal('ounce'), value: z.number() }),
      dimensions: z.object({
        unit: z.literal('inch'),
        length: z.number(),
        width: z.number(),
        height: z.number(),
      }),
      insured_value: z
        .object({ currency: z.literal('usd'), amount: z.number() })
        .optional(),
      insurance_opted_out: z.boolean().optional(),
    }),
  }).optional(),
  shipping_costs: z.array(z.object({
    cost: z.number(),
    code: z.string(),
    name: z.string()
  })).optional(),
  sku: z.string(),
  slug: z.string(),
  statuses: z.array(z.object({
    created_at: z.number(),
    status: z.string()
  })).optional(),
  stock: z.number(),
  taxable: z.boolean(),
  thumbnail: z.string(),
  title: z.string(),
  gearMaster: z.array(z.string()).optional(),
  counter: z.object({
    views: z.number().optional()
  }).optional(),
  external_id: z.number().optional(),
  external_source: z.string().optional(),
  rank_score: z.number().default(0)
});

export const draftProductDocumentSchema = productDocumentSchema.partial();
export type ProductDocument = z.infer<typeof productDocumentSchema>;
